import type { AllAccount } from "../../../../services/api";

/**
 * Reading the banking screen the way a CA taking over the books would.
 *
 * Ported from master-admin's onboarding banks step, against the tenant's own
 * `bank_accounts` rows rather than a live Zoho read. The tenant is not a thin
 * client over Zoho here: every bank picker, the cash dashboard and the
 * statement pipeline read this table, so what it holds IS the answer, not a
 * cache of one.
 */

export type BankGroup = "bank" | "credit_card" | "cash" | "other";

export const BANK_GROUP_ORDER: BankGroup[] = [
  "bank",
  "credit_card",
  "cash",
  "other",
];

export const BANK_GROUP_LABEL: Record<BankGroup, string> = {
  bank: "Bank accounts",
  credit_card: "Credit cards",
  cash: "Cash",
  other: "Other",
};

/**
 * Zoho files everything on its Banking screen under one of three types, and
 * anything else lands in "Other" rather than silently reading as a bank
 * account.
 *
 * `credit` is in here because the tenant and Zoho disagree on the word: the
 * add form posts `credit_card` to Zoho and stores `credit` locally
 * (`BankAccountsController::createBankAccount`), so both spellings are real
 * rows in this table and both are the same thing.
 */
export function bankGroupForType(type: string | null | undefined): BankGroup {
  if (type === "credit" || type === "credit_card") return "credit_card";
  if (type === "bank" || type === "cash") return type;
  return "other";
}

export function groupBankAccounts(
  accounts: AllAccount[],
): { group: BankGroup; accounts: AllAccount[] }[] {
  return BANK_GROUP_ORDER.map((group) => ({
    group,
    accounts: accounts.filter(
      (account) => bankGroupForType(account.account_type) === group,
    ),
  })).filter((section) => section.accounts.length > 0);
}

/**
 * NO BALANCE COLUMN, deliberately, and this is where master-admin's banks step
 * and this screen part company.
 *
 * Over there the figures are read live from Zoho. Here `bank_accounts.balance`
 * is a column nothing writes: the block in
 * `CashAndBankController::getAllAccounts` that would fill it from Zoho is
 * commented out, and every row on a real tenant reads 0.00. Rendering that
 * would put "₹0.00" against five funded accounts and call it a balance.
 *
 * Cash & Bank is where the money question belongs anyway. This screen answers
 * "is this account set up right", and the account number below is the field
 * that decides it.
 */

/**
 * The account number, when the name is not already carrying it.
 *
 * The naming convention here puts it in the name — "HDFC Current A/c -
 * 50200011350252" — so printing it beside that shows the same digits twice.
 * Credit cards are named without one ("Abbas Credit Card") and hold a masked
 * number of their own, which is worth seeing. So: show it only when it is not
 * already in the name, comparing on digits so the mask's spaces and X's do not
 * make a match look like a miss.
 */
export function accountNumberAside(account: AllAccount): string | null {
  const number = (account.account_number ?? "").trim();
  if (number === "") return null;

  const digits = number.replace(/\D/g, "");
  const nameDigits = (account.bank_name ?? "").replace(/\D/g, "");
  if (digits !== "" && nameDigits.includes(digits)) return null;

  return number;
}

/**
 * Whether a PDF statement can be uploaded against this account.
 *
 * NOT SURFACED on the settings panel — the user's call, 2026-08-08: that screen
 * lists accounts and adds one, and where an account number is needed belongs to
 * the screen that needs it. Kept because the constraint is real and easy to
 * rediscover the hard way: `DataController::uploadBankStatement` refuses the
 * upload outright when the row has no `account_number`, and then checks the
 * number actually appears on page one of the statement.
 *
 * Cash accounts are exempt: they have no number to hold and no statement to
 * upload.
 */
export function blocksStatementUpload(account: AllAccount): boolean {
  if (bankGroupForType(account.account_type) === "cash") return false;
  return (account.account_number ?? "").trim() === "";
}

/**
 * The display name, trimmed.
 *
 * Live data carries a trailing newline on at least one row ("HDFC Current A/c
 * - 50200011350252\n"), which came in from an import and renders as a stray
 * line break. Trimming here rather than migrating the column: the row is what
 * Zoho matched on, and this screen only has to read it.
 */
export function accountLabel(account: AllAccount): string {
  return (account.bank_name ?? "").trim() || account.zoho_bankaccountid;
}
